import { css, html, LitElement } from 'lit'
import { property } from 'lit/decorators.js'
import { safeCustomElement } from '@/utilities/custom-element'
import type { TextAs, TextVariant } from './types'

const DEFAULT_TAG_MAP: Record<string, TextAs> = {
  paragraph: 'p',
  text: 'span',
  label: 'span',
}

@safeCustomElement('mdc-text')
export class MdcText extends LitElement {
  @property({ type: String }) variant: TextVariant = 'text'
  @property({ type: String }) size = 'md'
  @property({ type: Boolean }) italic = false
  @property({ type: String }) as?: TextAs

  static styles = css`
    :host {
      display: block;
    }

    :host([variant='text']),
    :host([variant='label']) {
      display: inline;
    }

    .mdc-text {
      margin: 0;
      padding: 0;
      font-family: var(--typography-font-family-sans);
      font-weight: 450;
    }

    .mdc-text--italic {
      font-style: italic;
    }

    /* Paragraph variant (line-height 1.5, multi-line body copy) */
    .paragraph--lg {
      font-size: var(--typography-font-size-125);
      line-height: 1.5;
    }

    .paragraph--md {
      font-size: var(--typography-font-size-100);
      line-height: 1.5;
    }

    .paragraph--sm {
      font-size: var(--typography-font-size-087-and-half);
      line-height: 1.5;
    }

    .paragraph--xs {
      font-size: var(--typography-font-size-075);
      line-height: 1.5;
    }

    /* Text variant (line-height 1.3, compact UI text) */
    .text--md {
      font-size: var(--typography-font-size-100);
      line-height: 1.3;
    }

    .text--sm {
      font-size: var(--typography-font-size-087-and-half);
      line-height: 1.3;
    }

    .text--xs {
      font-size: var(--typography-font-size-075);
      line-height: 1.3;
    }

    .text--2xs {
      font-size: var(--typography-font-size-062-and-half);
      line-height: 1.2;
    }

    .text--3xs {
      font-size: var(--typography-font-size-050);
      line-height: 1.2;
    }

    /* Label variant (uppercase, tracked) */
    .label--sm {
      font-size: var(--typography-font-size-068-and-three-quatars);
      line-height: 1.3;
      letter-spacing: 0.03438rem;
      text-transform: uppercase;
    }

    .label--xs {
      font-size: var(--typography-font-size-062-and-half);
      line-height: 1.3;
      letter-spacing: 0.03125rem;
      text-transform: uppercase;
    }
  `

  render() {
    const tag = this.as ?? DEFAULT_TAG_MAP[this.variant] ?? 'span'
    const sizeClass = `${this.variant}--${this.size}`
    const italicClass = this.italic ? 'mdc-text--italic' : ''
    const classes = `mdc-text ${sizeClass} ${italicClass}`.trim()

    return this._renderTag(tag, classes)
  }

  private _renderTag(tag: string, classes: string) {
    switch (tag) {
      case 'p':
        return html`<p class=${classes}><slot></slot></p>`
      case 'span':
        return html`<span class=${classes}><slot></slot></span>`
      case 'label':
        return html`<label class=${classes}><slot></slot></label>`
      case 'div':
        return html`<div class=${classes}><slot></slot></div>`
      case 'em':
        return html`<em class=${classes}><slot></slot></em>`
      case 'strong':
        return html`<strong class=${classes}><slot></slot></strong>`
      default:
        return html`<span class=${classes}><slot></slot></span>`
    }
  }
}
